import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $findMatchingParent, mergeRegister } from '@lexical/utils';
import {
  $createParagraphNode,
  $getRoot,
  $getSelection,
  $isElementNode,
  $isRangeSelection,
  COMMAND_PRIORITY_LOW,
  DELETE_CHARACTER_COMMAND,
  DELETE_LINE_COMMAND,
  DELETE_WORD_COMMAND,
  REMOVE_TEXT_COMMAND,
  LexicalNode,
  PointType,
  RangeSelection,
} from 'lexical';
import { useEffect } from 'react';

function $isPointAtEdge(
  point: PointType,
  leaf: LexicalNode,
  edge: 'start' | 'end',
): boolean {
  const node = point.getNode();
  if (!node.is(leaf) && !($isElementNode(node) && node.isParentOf(leaf))) {
    return false;
  }
  if (edge === 'start') return point.offset === 0;
  return (
    point.offset ===
    ($isElementNode(node) ? node.getChildrenSize() : node.getTextContentSize())
  );
}

function $isWholeDocumentSelected(selection: RangeSelection): boolean {
  if (selection.isCollapsed()) return false;
  const root = $getRoot();
  const first = root.getFirstDescendant();
  const last = root.getLastDescendant();
  if (!first || !last) return false;
  const [start, end] = selection.isBackward()
    ? [selection.focus, selection.anchor]
    : [selection.anchor, selection.focus];
  return (
    $isPointAtEdge(start, first, 'start') && $isPointAtEdge(end, last, 'end')
  );
}

function $resetFormatIfLineEmpty(): void {
  const selection = $getSelection();
  if (!$isRangeSelection(selection) || !selection.isCollapsed()) return;
  const block = $findMatchingParent(
    selection.anchor.getNode(),
    (node) => $isElementNode(node) && !node.isInline(),
  );
  if (!$isElementNode(block) || block.getTextContent() !== '') return;
  selection.setFormat(0);
  selection.setStyle('');
  block.setTextFormat(0);
  block.setTextStyle('');
}

/**
 * 지우기 뒤 정리
 * - 전체 선택 후 지우면 목록(1.)·서식 없이 빈 문단 하나만 남김
 * - 줄의 글을 다 지우면 그 줄 서식(굵게·색·크기 등)도 지워 새로 쓸 때 이어지지 않게 함
 */
export default function DeleteResetPlugin(): null {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    const onDelete = () => {
      const selection = $getSelection();
      if (!$isRangeSelection(selection)) return false;
      const wholeDocument = $isWholeDocumentSelected(selection);
      // 기본 지우기 처리가 끝난 뒤 실행됨
      editor.update(() => {
        if (wholeDocument) {
          const paragraph = $createParagraphNode();
          $getRoot().clear().append(paragraph);
          paragraph.select();
          return;
        }
        $resetFormatIfLineEmpty();
      });
      return false;
    };

    return mergeRegister(
      ...[
        DELETE_CHARACTER_COMMAND,
        DELETE_WORD_COMMAND,
        DELETE_LINE_COMMAND,
        REMOVE_TEXT_COMMAND,
      ].map((command) =>
        editor.registerCommand(command, onDelete, COMMAND_PRIORITY_LOW),
      ),
    );
  }, [editor]);

  return null;
}
